import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import Loadable from '../layouts/full/shared/loadable/Loadable';
import PrivateRoute from './PrivateRoute';
import IndexRedirect from './IndexRedirect';
import RequirePasswordChangeGate from 'src/components/auth/RequirePasswordChangeGate';
import { ROLE_NAMES } from 'src/utils/roles';

/* ***Layouts**** */
const FullLayout = Loadable(lazy(() => import('../layouts/full/FullLayout')));
const BlankLayout = Loadable(lazy(() => import('../layouts/blank/BlankLayout')));

/* ****Receta Médica***** */
const MedicationList = Loadable(lazy(() => import('../views/medication/MedicationList')));
const MedicalRecord = Loadable(lazy(() => import('../views/pages/MedicalRecord')));
const PrescriptionCreate = Loadable(lazy(() => import('../views/prescription/PrescriptionCreate')));
const PrescriptionHistory = Loadable(lazy(() => import('../views/prescription/PrescriptionHistory')));
const ActivePrescriptions = Loadable(lazy(() => import('../views/prescription/ActivePrescriptions')));
const DiagnosisHistory = Loadable(lazy(() => import('../views/prescription/DiagnosisHistory')));
const PatientHistory = Loadable(lazy(() => import('../views/prescription/PatientHistory')));
const CertificateUpdate = Loadable(lazy(() => import('../views/certificate/CertificateUpdate')));
const DesktopSigner = Loadable(lazy(() => import('../views/certificate/DesktopSigner')));
const MyMedications = Loadable(lazy(() => import('../views/medication/MyMedications')));
const MedicationTakenHistory = Loadable(lazy(() => import('../views/medication/MedicationTakenHistory')));
const MyMedicalRecord = Loadable(lazy(() => import('../views/medical-record/MyMedicalRecord')));
const MyProfile = Loadable(lazy(() => import('../views/profile/MyProfile')));
const Branches = Loadable(lazy(() => import('../views/pharmacy/Branches')));
const Users = Loadable(lazy(() => import('../views/pharmacy/Users')));
const PharmacySettings = Loadable(lazy(() => import('../views/pharmacy/Settings')));
const Dispensing = Loadable(lazy(() => import('../views/pharmacy/Dispensing')));
const DispensingHistory = Loadable(lazy(() => import('../views/pharmacy/DispensingHistory')));
const PharmacyReports = Loadable(lazy(() => import('../views/pharmacy/Reports')));
const Reports = Loadable(lazy(() => import('../views/reports/Reports')));
const PatientReports = Loadable(lazy(() => import('../views/reports/PatientReports')));

/* ****Autenticación***** */
const Login = Loadable(lazy(() => import('../views/authentication/auth/Login')));
const Register = Loadable(lazy(() => import('../views/authentication/auth/Register')));
const ProfessionalRegister = Loadable(
  lazy(() => import('../views/authentication/auth/ProfessionalRegister'))
);
const SelectRegister = Loadable(
  lazy(() => import('../views/authentication/auth/SelectRegister'))
);
const PharmacyRegister = Loadable(
  lazy(() => import('../views/authentication/auth/PharmacyRegister'))
);
const ForgotPassword = Loadable(
  lazy(() => import('../views/authentication/auth/ForgotPassword'))
);
const ResetPassword = Loadable(
  lazy(() => import('../views/authentication/auth/ResetPassword'))
);
const Error = Loadable(lazy(() => import('../views/authentication/Error')));
const Maintenance = Loadable(lazy(() => import('../views/authentication/Maintenance')));

const Router = [
  {
    path: '/',
    element: (
      <PrivateRoute>
        <RequirePasswordChangeGate>
          <FullLayout />
        </RequirePasswordChangeGate>
      </PrivateRoute>
    ),
    children: [
      { path: '/', element: <IndexRedirect /> },
      {
        path: '/medication/list',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PROFESSIONAL]}>
            <MedicationList />
          </PrivateRoute>
        ),
      },
      {
        path: '/medical-record',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PROFESSIONAL]}>
            <MedicalRecord />
          </PrivateRoute>
        ),
      },
      { path: '*', element: <Navigate to="/auth/404" /> },
      {
        path: '/prescription/create',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PROFESSIONAL]}>
            <PrescriptionCreate />
          </PrivateRoute>
        ),
      },
      {
        path: '/prescription/history',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PROFESSIONAL, ROLE_NAMES.PATIENT]}>
            <PrescriptionHistory />
          </PrivateRoute>
        ),
      },
      {
        path: '/prescription/active',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PATIENT]}>
            <ActivePrescriptions />
          </PrivateRoute>
        ),
      },
      {
        path: '/prescription/diagnosis-history',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PATIENT]}>
            <DiagnosisHistory />
          </PrivateRoute>
        ),
      },
      {
        path: '/prescription/patient-history',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PROFESSIONAL]}>
            <PatientHistory />
          </PrivateRoute>
        ),
      },
      {
        path: '/medication/mine',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PATIENT]}>
            <MyMedications />
          </PrivateRoute>
        ),
      },
      {
        path: '/medication/history',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PATIENT]}>
            <MedicationTakenHistory />
          </PrivateRoute>
        ),
      },
      {
        path: '/medical-record/mine',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PATIENT]}>
            <MyMedicalRecord />
          </PrivateRoute>
        ),
      },
      {
        path: '/reports',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PROFESSIONAL]}>
            <Reports />
          </PrivateRoute>
        ),
      },
      {
        path: '/reports/mine',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PATIENT]}>
            <PatientReports />
          </PrivateRoute>
        ),
      },
      {
        path: '/certificate/update',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PROFESSIONAL, ROLE_NAMES.PHARMACY]}>
            <CertificateUpdate />
          </PrivateRoute>
        ),
      },
      {
        path: '/certificate/desktop-signer',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PROFESSIONAL, ROLE_NAMES.PHARMACY]}>
            <DesktopSigner />
          </PrivateRoute>
        ),
      },
      {
        path: '/pharmacy/dispensing',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PHARMACY]}>
            <Dispensing />
          </PrivateRoute>
        ),
      },
      {
        path: '/pharmacy/dispensing-history',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PHARMACY]}>
            <DispensingHistory />
          </PrivateRoute>
        ),
      },
      {
        path: '/pharmacy/reports',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PHARMACY]}>
            <PharmacyReports />
          </PrivateRoute>
        ),
      },
      {
        path: '/pharmacy/branches',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PHARMACY]}>
            <Branches />
          </PrivateRoute>
        ),
      },
      {
        path: '/pharmacy/users',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PHARMACY]}>
            <Users />
          </PrivateRoute>
        ),
      },
      {
        path: '/pharmacy/settings',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PHARMACY]}>
            <PharmacySettings />
          </PrivateRoute>
        ),
      },
      {
        path: '/user-profile',
        element: (
          <PrivateRoute allowedRoles={[ROLE_NAMES.PROFESSIONAL, ROLE_NAMES.PATIENT, ROLE_NAMES.PHARMACY]}>
            <MyProfile />
          </PrivateRoute>
        ),
      },
    ],
  },
  {
    path: '/',
    element: <BlankLayout />,
    children: [
      { path: '/auth/404', element: <Error /> },
      { path: '/auth/login', element: <Login /> },
      { path: '/auth/select-register', element: <SelectRegister /> },
      { path: '/auth/register', element: <Register /> },
      { path: '/auth/register-professional', element: <ProfessionalRegister /> },
      { path: '/auth/register-pharmacy', element: <PharmacyRegister /> },
      { path: '/auth/forgot-password', element: <ForgotPassword /> },
      { path: '/auth/reset-password', element: <ResetPassword /> },
      { path: '/auth/maintenance', element: <Maintenance /> },
      { path: '*', element: <Navigate to="/auth/404" /> },
    ],
  },
];

export default Router;
